import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Button from 'core/elements/button'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import ListTableField from 'core/components/validatedForm/ListTableField'
import { useAppSelector } from 'app/store'
import {
  attachVolume,
  detachVolume,
  getVolumeById,
  listNonPaginatedVolumes,
} from 'openstack/components/storage/volumes/actions'
import { nonPaginatedVolumesSelector } from 'openstack/components/storage/volumes/selectors'
import { humanReadableSize } from 'openstack/helpers'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import Info from 'core/components/validatedForm/Info'
import { getCurrentUser } from 'openstack/helpers'
import { formatTimestampToLocal, hasOtherUserVm } from './helpers'
import ExternalLink from 'core/components/ExternalLink'
import { VOLUME_BACKED_VM_STRING } from './selectors'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import { VolumeSourceCell } from '../storage/volumes/volume-details/VolumeSourceCell'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { attachVolumeDoc } from 'app/docLinks'
import { convertToTitleCase } from '../infrastructure/blueprint/helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  nestedModal: {
    zIndex: 20000,
    '& .modal-body .progress-root': {
      height: 'auto',
      minHeight: 'auto',
    },
  },
  warning: {
    marginBottom: 16,
    border: `1px solid ${theme.palette.yellow[500]}`,
    background: theme.palette.yellow[100],
  },
  button: {
    width: 100,
  },
}))

const enum VolumeStatus {
  Reserved = 'reserved',
  Detaching = 'detaching',
  Attaching = 'attaching',
  Available = 'available',
  InUse = 'in-use',
}
const InProgressStates = [VolumeStatus.Detaching, VolumeStatus.Attaching, VolumeStatus.Reserved]

const VolumeActionButton = ({
  volume,
  detachVolumeFromVM,
  attachVolumeToVm,
  resetAll,
  localLoading,
  setLocalLoading,
}) => {
  const classes = useStyles()
  const [modalOpen, setModalOpen] = useState(false)
  const volumeInUse = volume.status === VolumeStatus.InUse
  const actionInProgress = InProgressStates.includes(volume.status) || localLoading[volume.id]

  const onButtonSubmit = useCallback(() => {
    setLocalLoading((prev) => ({ ...prev, [volume.id]: true }))
    if (volumeInUse) {
      detachVolumeFromVM(volume)
    } else {
      attachVolumeToVm(volume)
    }
    setModalOpen(false)
  }, [volumeInUse, detachVolumeFromVM, attachVolumeToVm])

  return (
    <>
      <ModalForm
        title={volumeInUse ? 'Detach Volume' : 'Attach Volume'}
        onSubmit={onButtonSubmit}
        onClose={() => setModalOpen(false)}
        submitTitle={volumeInUse ? 'Detach' : 'Attach'}
        panel="dialog"
        pageClassName={classes.nestedModal}
        open={modalOpen}
      >
        {volumeInUse ? (
          <Text variant="body2">
            Are you sure you want to detach this volume <b>{volume.name || volume.id}</b>? Detaching
            will make the volume unavailable to the virtual machine and may disrupt any running
            proceses dependent on it.
          </Text>
        ) : (
          <Text variant="body2">
            Are you sure you want to attach this volume <b>{volume.name || volume.id}</b> to the
            selected virtual machine?
          </Text>
        )}
      </ModalForm>
      <Button
        id="attachDetachVolume"
        size="small"
        className={classes.button}
        disabled={actionInProgress}
        loading={actionInProgress}
        onClick={() => {
          setModalOpen(true)
        }}
        variant={volumeInUse ? 'tertiary' : 'primary'}
        icon={volumeInUse ? 'trash-alt' : 'plus'}
        tooltip={actionInProgress ? convertToTitleCase(volume?.status) : ''}
      >
        {volumeInUse ? 'Detach' : 'Attach'}
      </Button>
    </>
  )
}

const volumeColumns = ({
  detachVolumeFromVM,
  attachVolumeToVm,
  resetAll,
  localLoading,
  setLocalLoading,
}) => [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'id',
    label: 'UUID',
  },
  {
    id: 'hostName',
    label: 'Host',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'backend',
    label: 'Volume Backend',
    visible: !isAdmin(),
    display: false,
  },
  {
    id: 'tenantName',
    label: 'Tenant',
    display: false,
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'volume_type',
    label: 'Type',
    display: false,
  },
  {
    id: 'device',
    label: 'Device',
    display: false,
  },
  {
    id: 'snapshots',
    label: 'Snapshots',
    render: (snapshots) => snapshots?.length,
    display: false,
  },
  {
    id: 'size',
    label: 'Size',
    render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
  },
  {
    id: 'bootable',
    label: 'Bootable',
  },
  {
    id: 'multiattach',
    label: 'Multiattach',
    render: (val) => (val ? 'True' : 'False'),
    display: false,
  },
  {
    id: 'source',
    label: (
      <TitleWithInfoIcon
        titleText="Source"
        tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
      />
    ),
    render: (val, item) => <VolumeSourceCell item={item} />,
    display: false,
  },
  {
    id: 'created_at',
    label: 'Age',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
  {
    id: 'metadata',
    label: 'Metadata',
    render: (value) => createResourceLabelsCell({ type: 'table', separator: '=' }),
    display: false,
  },
  {
    id: 'description',
    label: 'Action',
    render: (_description, item) => {
      return (
        <VolumeActionButton
          volume={item}
          detachVolumeFromVM={detachVolumeFromVM}
          attachVolumeToVm={attachVolumeToVm}
          resetAll={resetAll}
          localLoading={localLoading}
          setLocalLoading={setLocalLoading}
        />
      )
    },
  },
]

export default function ManageVolumesModal({ rows: vms, onClose, isOpenModal }) {
  const vm = vms?.[0]
  const usesEphemeralStorage = vm?.imageName !== VOLUME_BACKED_VM_STRING
  const classes = useStyles()
  const { role: userRole, userId } = getCurrentUser()

  // Store interval handle using ref
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null)
  const [modalOpen, setModalOpen] = useState(isOpenModal)
  const [localLoading, setLocalLoading] = useState<Record<string, boolean>>({})

  // Update volumes
  const {
    update: attachVolumeFn,
    updating: attachingVolume,
    error: attachVolumeError,
    reset: resetAttachVolume,
  } = useUpdateAction(attachVolume)
  const {
    update: detachVolumeFn,
    updating: detachingVolume,
    error: detachingVolumeError,
    reset: resetDetachVolume,
  } = useUpdateAction(detachVolume)

  // Fetch volumes
  const { loading: loadingVolumes, reload } = useListAction(listNonPaginatedVolumes, {
    ignoreDependency: true,
  })
  const volumes = useAppSelector(nonPaginatedVolumesSelector)
  const availableVolumes = useMemo(() => {
    return volumes.filter(
      (volume) => volume.status === VolumeStatus.Available || volume?.attachedVm?.id === vm.id,
    )
  }, [volumes, vm])

  const inProgressVolumeIds = useMemo(() => {
    return availableVolumes
      ?.filter((volume) => InProgressStates?.includes(volume?.status))
      ?.map((volume) => volume?.id)
  }, [availableVolumes])

  // Reload volumes **once** when modal opens
  useEffect(() => {
    if (modalOpen) {
      reload(true, false)
    }
  }, [])

  const resetAll = useCallback(() => {
    setLocalLoading({})
    resetAttachVolume()
    resetDetachVolume()
  }, [resetAttachVolume, resetDetachVolume])

  // Poll in-progress volumes using setInterval
  const MAX_RETRIES = 10
  const POLL_INTERVAL = 10000

  useEffect(() => {
    const resetInterval = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current)
        intervalIdRef.current = null
      }
    }

    // No in-progress volumes - nothing to poll
    if (inProgressVolumeIds.length === 0) {
      resetAll()
      resetInterval()
      return undefined
    }

    // Already polling
    if (intervalIdRef.current) return undefined

    // Set loading state for in-progress volumes
    setLocalLoading((prev) => {
      const next = { ...prev }
      inProgressVolumeIds.forEach((id) => (next[id] = true))
      return next
    })

    let retryCount = 0

    const pollVolumes = async () => {
      retryCount++

      try {
        const fetchedVolumes = await Promise.all(
          inProgressVolumeIds.map((id) => getVolumeById({ id })),
        )

        // Update loading state based on fetched status
        setLocalLoading((prev) => {
          const next = { ...prev }
          fetchedVolumes.forEach((volume) => {
            if (
              volume?.status === VolumeStatus.Available ||
              volume?.status === VolumeStatus.InUse
            ) {
              next[volume.id] = false
            }
          })
          return next
        })

        // Check if all volumes are done
        const allDone = fetchedVolumes.every(
          (v) => v?.status === VolumeStatus.Available || v?.status === VolumeStatus.InUse,
        )

        if (allDone || retryCount >= MAX_RETRIES) {
          resetAll()
          resetInterval()
        }
      } catch (err) {
        console.error('Error polling volumes:', err)
        if (retryCount >= MAX_RETRIES) {
          resetAll()
          resetInterval()
        }
      }
      return undefined
    }

    // Start polling
    intervalIdRef.current = setInterval(pollVolumes, POLL_INTERVAL)

    // Cleanup on unmount or when inProgressVolumeIds changes
    return () => {
      resetInterval()
    }
  }, [inProgressVolumeIds.join(',')])

  // Attach volume
  const attachVolumeToVm = useCallback(
    async ({ id }) => {
      const body = { volumeAttachment: { volumeId: id } }
      const response = await attachVolumeFn({ vmId: vm.id, id, body })
      if (!response.success) {
        setLocalLoading((prev) => ({ ...prev, [id]: false }))
      }
    },
    [vm, attachVolumeFn],
  )

  // Detach volume
  const detachVolumeFromVM = useCallback(
    async ({ id }) => {
      const response = await detachVolumeFn({ vmId: vm.id, volumeId: id })
      if (!response.success) {
        setLocalLoading((prev) => ({ ...prev, [id]: false }))
      }
    },
    [vm, detachVolumeFn],
  )

  const onModalClose = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = null
    }
    resetAll()
    onClose()
    setModalOpen(false)
  }, [onClose, resetAll])

  return (
    <ModalForm
      open
      title={`Manage Volumes`}
      onClose={onModalClose}
      submitting={attachingVolume || detachingVolume}
      error={attachVolumeError || detachingVolumeError}
      submitTitle={`Attach Volumes`}
      maxWidth={1200}
      closeText="Close"
    >
      <ServiceUnhealthyInfo action="Volume Actions" hypervisor blockStorage />
      {usesEphemeralStorage && (
        <Info className={classes.warning} error>
          This virtual machine uses an ephemeral root disk. Read{' '}
          <ExternalLink url={attachVolumeDoc}>this article</ExternalLink> to understand the{' '}
          <b>caveats of attaching volumes</b> to such VMs.
        </Info>
      )}
      <FormFieldSection title={`Volumes to attach or detach from VM: ${vm.name || vm.id}`}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <ListTableField
          id="attachDetachVolumes"
          uniqueIdentifier="id"
          searchTargets={['name', 'id', 'status']}
          data={availableVolumes}
          loading={loadingVolumes}
          columns={volumeColumns({
            attachVolumeToVm,
            detachVolumeFromVM,
            resetAll,
            localLoading,
            setLocalLoading,
          })}
          onReload={() => reload(true, true)}
          emptyText="No volumes available"
          showCheckboxes={false}
        />
      </FormFieldSection>
    </ModalForm>
  )
}
